/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import type { TablePaginationConfig } from 'antd/es/table'
import { ExclamationCircleOutlined, EyeOutlined, UploadOutlined, WarningTwoTone, CalendarOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import {
  Batch,
  BatchMapData,
  ManageTemple,
  Product,
  MetaData,
  SKUFilters,
  Users,
  GenieVideoQCResponse,
  GenieVideoQCData,
  Store
} from '@a4b/api/src/modules/Monetisation/Offerings/types'
import { YODA_CDN_URL, YODA_PRESIGN_URL } from '@/utils/constants'
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/molecules/S3Upload'

const { Option } = Select;
const { Search } = Input

interface Props {
  itemList: Batch[]
  onSearch: (value: string) => void
  onViewBatchClick: (batch: Batch) => void
  skuFilters: SKUFilters[] | undefined
  onTabChange: (activeTab: string) => void
  isComplete: boolean
  onCompleteChange: () => void
  reducedItemCount: any
  batchCount: number
  handleCompletedVideoPreview: (videoUrl: string, product_type: string, record: any) => void
  languageSelected: string
  isGenerateBatch: boolean
  handleClickUploadMedia: () => void
  showUploadButton: boolean
  selectionType: string
  rowSelection: any
  hubAssociates: any
  updateAssociateForBatch: (batchId: string, associateId: string) => void
  handleStoreMediaModal: () => void
  onScheduleDateChange: (date: moment.Moment | null) => void
  onAssociateIdChange: (associateId: string) => void
  onStoreIdChange: (storeId: string) => void
  handleGenerateBatchModal: () => void
  storesData: Store[] | undefined
  handleTableChange: (paginationParams: TablePaginationConfig) => void
  isTempleListingEnabled: boolean
  loading: boolean
  onBatchStatusChange: (status: string) => void
  pagination: TablePaginationConfig
  isBulkAssignModalVisible: boolean
  handleCloseBulkAssignModal: () => void
  handleBulkAssign: (associateId: string) => void
  handleBulkAssignModal: () => void
}

const Container = styled(Card)`
  display: flex;
  flex-direction: column;

  .ant-card-body {
    flex-grow: 1;
  }
`
const BatchListPage: React.FC<Props> = ({
  itemList,
  onSearch,
  onViewBatchClick,
  pagination,
  skuFilters,
  onTabChange,
  isComplete,
  onCompleteChange,
  reducedItemCount,
  batchCount,
  handleCompletedVideoPreview,
  languageSelected,
  isGenerateBatch,
  handleClickUploadMedia,
  showUploadButton,
  selectionType,
  rowSelection,
  hubAssociates,
  updateAssociateForBatch,
  handleStoreMediaModal,
  onScheduleDateChange,
  onAssociateIdChange,
  onStoreIdChange,
  handleGenerateBatchModal,
  handleTableChange,
  storesData,
  isTempleListingEnabled,
  loading,
  onBatchStatusChange,
  isBulkAssignModalVisible,
  handleCloseBulkAssignModal,
  handleBulkAssign,
  handleBulkAssignModal,
}) => {
  const [isVideoQCModalVisible, setIsVideoQCModalVisible] = React.useState<boolean>(false)
  const [videoQCResponseData, setVideoQCResponseData] = React.useState<GenieVideoQCResponse[]>([])
  const [selectedBatch, setSelectedBatch] = React.useState<string | null>(null)

  const showVideoQCModal = (batch: Batch) => () => {
    const genieVideoQCData = (batch?.meta_data as { genie_video_qc_data?: GenieVideoQCData })?.genie_video_qc_data
    const genieVideoQCResponse = genieVideoQCData?.genie_video_qc_response ?? []

    setSelectedBatch(batch.batch_code)
    setVideoQCResponseData(genieVideoQCResponse)
    setIsVideoQCModalVisible(true);
  }

  const handleCancel = () => {
    setIsVideoQCModalVisible(false)
  }

  const commonColumns = [
    {
      title: 'Batch Id/Batch Code',
      dataIndex: 'batch_id',
      key: 'batch_id',
      width: '15%',
      filtered: true,
      render: (text: any, record: Batch) => <>
        {record?.is_puja_batch && <Tag color='blue'>{record?.is_puja_batch && 'Puja'}</Tag>}
        <span>{record?.batch_code || record?.batch_id}</span>
      </>
    },
    {
      title: 'No of Items',
      dataIndex: 'no_of_items',
      key: 'no_of_items',
      width: '4%',
    },
    {
      title: 'SKU Name',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text: any, record: Batch) => <span>{record?.inventory_names?.[languageSelected] || text}</span>,
    },
    {
      title: 'SLA Breach Status',
      dataIndex: 'is_sla_breached',
      key: 'is_sla_breached',
      width: '9%',
      render: (text: boolean, record: Batch) => {
        if (text) {
          return <Tag icon={<ExclamationCircleOutlined />} color='#f50'>SLA Breach</Tag>
        } else {
          return 'N/A'
        }
      }
    },
    {
      title: 'Store Name',
      dataIndex: 'store_name',
      key: 'store_name',
      width: '13%',
      render: (text: any, record: Batch) => <span>{record?.store_names?.[languageSelected] || text}</span>,
    },
    {
      title: 'Product Type',
      dataIndex: 'product_type',
      key: 'product_type',
      width: '6%',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '8%',
      render: (text: any) => (
        <span>{moment.unix(text).format('ddd DD-MMM-YYYY, hh:mm A')}</span>
      ),
    },
    {
      title: 'Schedule Date',
      dataIndex: 'schedule_date_unix',
      key: 'schedule_date_unix',
      width: '8%',
      render: (text: any, record: Batch) => {
        const unixTimestamp = record?.schedule_date_unix;
        const now = moment().unix();
        return unixTimestamp !== 0 ? (
          <div>
            <span>{moment.unix(unixTimestamp)?.format('ddd DD-MMM-YYYY, hh:mm A')}</span>
            {unixTimestamp > now && (
              <Tag icon={<CalendarOutlined />} color='green' style={{ marginTop: '4px' }}>PreBooking</Tag>
            )}
          </div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '7%',
      render: (text: any) => <span>{text}</span>,
    },
    {
      title: 'Hub Associate',
      dataIndex: 'associate_id',
      key: 'associate_id',
      width: '11%',
      render: (text: any, record: Batch, index: number) => (
        <Select value={text} onChange={(value) => updateAssociateForBatch(record?.batch_id, value)} style={{ width: '100%', maxWidth: '180px' }} disabled={record?.status !== 'created'}>
          {hubAssociates?.map((associate: Users) => (
            <Option key={associate?.id} value={associate?.id}>
              {associate?.name} {associate?.user_type === 'hub_manager' && (
                <span style={{ color: 'orange' }}>(Hub Manager)</span>
              )}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Video QC Status',
      key: 'video_qc_status',
      render: (text: any, record: Batch) => {
        const genieVideoQCData = (record?.meta_data as { genie_video_qc_data?: GenieVideoQCData })?.genie_video_qc_data
        const videoQCStatus = genieVideoQCData?.video_qc_status

        if (!videoQCStatus) {
          return null;
        }

        let statusTag;
        if (videoQCStatus === 'invalid_video') {
          statusTag = <Tag color="red" style={{ cursor: 'pointer' }} onClick={showVideoQCModal(record)}>Invalid Video</Tag>
        } else if (videoQCStatus === 'semi_valid_video') {
          statusTag = <Tag color="orange" style={{ cursor: 'pointer' }} onClick={showVideoQCModal(record)}>Semi-Valid Video</Tag>
        } else if (videoQCStatus === 'valid_video') {
          statusTag = <Tag color="green" style={{ cursor: 'pointer' }} onClick={showVideoQCModal(record)}>Valid Video</Tag>
        } else if (videoQCStatus === 'super_invalid_video') {
          statusTag = <Tag color="#820014" style={{ cursor: 'pointer' }} onClick={showVideoQCModal(record)}>Super Invalid Video</Tag>
        }

        const columns = [
          {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
          },
          {
            title: 'Status',
            dataIndex: 'is_covered',
            key: 'status',
            render: (isCovered: boolean) => (isCovered ? 'Covered' : 'Not Covered'),
          },
          {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
          }
        ]

        return (
          <>
            {statusTag}
            <Modal
              title={`Offering Usernames QC Details for Batch Code: ${selectedBatch}`}
              visible={isVideoQCModalVisible}
              onCancel={handleCancel}
              footer={null}
              centered
              width={600}
              closable={true}
            >
              <Table
                bordered
                columns={columns}
                dataSource={videoQCResponseData}
              />
            </Modal>
          </>
        )
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (text: any, record: any, index: any) => {
        const videoUrl = record?.meta_data?.processed_video_url || record?.video_url;
        return (
          <Row>
            <Col span={12}>
              {record.status === 'completed' ? (
                videoUrl ? (
                  <Button
                    type='link'
                    onClick={() => handleCompletedVideoPreview(videoUrl, record?.media_type, record)}
                  >
                    <EyeOutlined />
                    Preview Video
                  </Button>
                ) : (
                  <span>Video not available</span>
                )
              ) : (
                <Button type='link' onClick={() => onViewBatchClick(record)}>
                  <EyeOutlined />
                  View Order
                </Button>
              )}
            </Col>
          </Row>
        )
      },
    },
  ]

  return (
    <Container style={{ width: '100%', height: '100%' }}>
      {showUploadButton && <Button
        type="primary"
        shape="circle"
        icon={<UploadOutlined />}
        size='large'
        style={{
          position: 'fixed', bottom: 40, right: 30,
          zIndex: 100,
          height: '50px',
          width: '50px'
        }}
        onClick={handleClickUploadMedia}
      />}
      <Space direction={'vertical'} style={{
        width: '100%'
      }}>
      </Space>
      <Row>
        <Col span={12}></Col>
        <Col span={12}>
          <Row justify='end'></Row>
        </Col>
      </Row>
      <Row
        justify="space-between"
        align="middle"
        wrap
        style={{ marginBottom: 24 }}
      >
        <Col flex="auto">
          <Space size="middle" wrap>
            <Search
              placeholder="Search by batch code"
              onSearch={(searchCriteria) => onSearch(searchCriteria)}
              style={{ width: 180 }}
              allowClear
            />
            <DatePicker
              onChange={(date) => onScheduleDateChange(date)}
              placeholder="Filter by schedule date"
              style={{ width: 180 }}
              allowClear
            />
            <Select
              placeholder="Filter by User"
              onChange={(associateId) => onAssociateIdChange(associateId)}
              style={{ width: 150 }}
              allowClear
            >
              <Select.Option value="assigned">
                <span style={{ color: 'green', fontWeight: 'bold' }}>Assigned</span>
              </Select.Option>
              <Select.Option value="unassigned">
                <span style={{ color: '#4682b4', fontWeight: 'bold' }}>Unassigned</span>
              </Select.Option>

              {hubAssociates?.map((associate: Users) => (
                <Select.Option key={associate.id} value={associate.id}>
                  {associate.name}
                  {associate.user_type === 'hub_manager' && (
                    <span style={{ color: 'orange' }}> (Hub Manager)</span>
                  )}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by status"
              onChange={(status) => onBatchStatusChange(status)}
              style={{ width: 150 }}
              allowClear
            >
              <Select.Option value="created">Created</Select.Option>
              <Select.Option value="processing">Processing</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="sla_breached">
                <span style={{ color: '#FF8C00', fontWeight: 'bold' }}>SLA Breached</span>
              </Select.Option>
              <Select.Option value="sla_not_breached">
                <span style={{ color: 'green', fontWeight: 'bold' }}>SLA Not Breached</span>
              </Select.Option>
            </Select>
            {isTempleListingEnabled && (
              <Select
                showSearch
                placeholder="Filter by Store"
                onChange={(storeId) => onStoreIdChange(storeId)}
                style={{ width: 200 }}
                allowClear
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {storesData?.map((store) => (
                  <Select.Option key={store.id} value={store.id} label={`${store.name} (${store.id})`}>
                    {store.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Space>
        </Col>
        <Col>
          <Space size="middle" wrap>


            {rowSelection.selectedRows && rowSelection.selectedRows.length > 0 && (
              <Button type='primary' onClick={handleBulkAssignModal}>
                Bulk Assign
              </Button>
            )}

            <Button type="primary" onClick={handleStoreMediaModal} style={{ width: 180 }}>
              Manage Store Media
            </Button>
            <Button type="primary" onClick={handleGenerateBatchModal} disabled={isGenerateBatch} style={{ width: 180 }}>
              Generate Batch
            </Button>
          </Space>
        </Col>
      </Row>

      <Row
        justify="space-between"
        align="middle"
        style={{
          marginBottom: 16,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Tabs
            onChange={(activeKey) => onTabChange(activeKey)}
            type="card"
            style={{ marginBottom: 0 }}
          >
            <Tabs.TabPane tab={`(${batchCount})All SKU`} key="all_sku" />
            {skuFilters?.map((product: SKUFilters) => (
              <Tabs.TabPane
                key={product.sku_id}
                tab={
                  reducedItemCount?.[product.sku_id]
                    ? `(${reducedItemCount?.[product.sku_id]})${product.sku_name}`
                    : product.sku_name
                }
              />
            ))}
          </Tabs>
        </Col>

        <Col flex="none" style={{ marginLeft: 12 }}>
          <Typography.Title level={5} style={{ margin: 0, whiteSpace: 'nowrap' }}>
            Show Completed Batch:{' '}
            <Switch checked={isComplete} onChange={onCompleteChange} />
          </Typography.Title>
        </Col>
      </Row>

      <Table
        rowSelection={{
          type: selectionType,
          ...rowSelection,
          getCheckboxProps: (record: Batch) => ({
            disabled: record.status !== 'created', // Disable checkbox for batches not in 'created' status
          }),
        }}
        rowKey={(record: Batch) => record.batch_id}
        columns={commonColumns}
        dataSource={itemList}
        pagination={pagination}
        onChange={handleTableChange}
      />
      {/* {pagination?.current && (
        <div style={{ display: 'flex', justifyContent: 'right', width: '100%', paddingTop: '10px' }}>
          <Button onClick={() => handlePageChange('previous')} disabled={pagination?.current === 1 || loading}>Prev</Button>
          <Button style={{ marginLeft: '5px', marginRight: '5px' }}>{String(pagination?.current)}</Button>
          <Button onClick={() => handlePageChange('next')} disabled={pagination?.is_last_page || loading}>Next</Button>
        </div>
      )} */}

    </Container>
  )
}

export default BatchListPage